"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, Search, Flame, ArrowRight, PhoneCall, ShieldCheck, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  id: "pipe-repair",
  title: "Pipe Repair & Burst Lines",
  description: "Fast response for burst pipes, noisy water hammers, frozen lines, and leaking joint repairs to stop structural damage immediately.",
  link: "/services/",
  badge: "24/7 Response",
  icon: Wrench,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png-wvc-sizes",
  features: "Pipe patching, repiping, burst line shutdown"
}, {
  id: "leak-detection",
  title: "Slab & Hidden Leak Detection",
  description: "Non-invasive electronic acoustic leak detection to pinpoint hidden underground, behind-wall, and slab leaks without destructive demolition.",
  link: "/problems/",
  badge: "Non-Invasive",
  icon: Search,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_service.png-wvc-sizes",
  features: "Thermal imaging, pressure testing, pinpoint accuracy"
}, {
  id: "water-heater",
  title: "Water Heater Service & Install",
  description: "Same-day water heater repair and replacement for tank and tankless systems. Fast fixes for no hot water, pilot outages, and tank leaks.",
  link: "/water-heater/",
  badge: "Same-Day Setup",
  icon: Flame,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_service.png-wvc-sizes",
  features: "Gas, electric, and tankless unit specialists"
}];
export default function FeaturedServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="featuredservices" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 font-semibold">
            Emergency Solutions
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Core Emergency Plumbing Services
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            Direct, reliable repairs in Jonesboro, GA. We arrive quickly with equipped trucks to stop leaks, restore hot water, and secure your home.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => {
          const Icon = service.icon;
          return <motion.div key={service.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <div>
                    <div className="relative aspect-16/9 w-full overflow-hidden rounded-t-xl bg-muted">
                      <img src={service.image} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      <div className="absolute top-3 right-3">
                        <Badge variant="default" className="shadow-xs">
                          {service.badge}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="pt-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary">
                          <Icon className="size-5" />
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Priority Service
                        </span>
                      </div>
                      <CardTitle className="text-xl md:text-2xl font-default text-card-foreground">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-sm mt-2">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0 pb-4">
                      <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md border border-border">
                        <span className="font-semibold text-foreground">Includes: </span>
                        {service.features}
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 flex flex-col gap-2.5">
                    <Button asChild className="w-full justify-between" variant="default">
                      <Link to={service.link}>
                        <span>Explore {service.title.split(" ")[0]} Details</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full justify-center">
                      <Link to="/contact/">
                        <PhoneCall className="size-4 mr-1.5" />
                        <span>Request Service</span>
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="mt-12 md:mt-16 bg-muted/60 border border-border rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start sm:items-center gap-4">
            <div className="p-3 bg-primary text-primary-foreground rounded-lg shrink-0">
              <Clock className="size-6" />
            </div>
            <div>
              <h3 className="font-default font-bold text-lg text-foreground">
                Facing an immediate water damage risk?
              </h3>
              <p className="text-sm text-muted-foreground">
                Our emergency dispatchers are on standby 24 hours a day across Clayton County.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0 w-full sm:w-auto">
            <div className="hidden lg:flex items-center gap-1.5 text-xs text-muted-foreground mr-2">
              <ShieldCheck className="size-4 text-primary" />
              <span>Licensed & Insured</span>
            </div>
            <Button asChild size="lg" variant="default" className="w-full sm:w-auto">
              <Link to="/contact/">Get Immediate Plumber</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}